
import { v4 as uuidv4 } from 'uuid';
import { EnrollmentEvent, EnrollmentEventType, EnrollmentStatus, EnrollmentEventDetails } from './types';
import { Program, Student } from '../types';
import { getStorageData, setStorageData } from '../adapter/storageUtils';
import { trackStudentRecordModification } from '../admin/adminActionTracking';

const ENROLLMENT_HISTORY_KEY = 'nalc_enrollment_history';

/**
 * Get all enrollment events for a student
 */
export const getStudentEnrollmentHistory = async (studentId: number): Promise<EnrollmentEvent[]> => {
  try {
    const allEvents = await getAllEnrollmentEvents();
    return allEvents.filter(event => event.studentId === studentId);
  } catch (error) {
    console.error('Error retrieving student enrollment history:', error);
    return [];
  }
};

/**
 * Get all enrollment events
 */
export const getAllEnrollmentEvents = async (): Promise<EnrollmentEvent[]> => {
  try {
    const events = await getStorageData<EnrollmentEvent[]>(ENROLLMENT_HISTORY_KEY);
    return events || [];
  } catch (error) {
    console.error('Error retrieving enrollment events:', error);
    return [];
  }
};

/**
 * Save an enrollment event
 */
export const saveEnrollmentEvent = async (event: EnrollmentEvent): Promise<EnrollmentEvent> => {
  try {
    const events = await getAllEnrollmentEvents();
    
    // Ensure the event has an ID
    const eventToSave: EnrollmentEvent = {
      ...event,
      id: event.id || uuidv4(),
      timestamp: event.timestamp || new Date().toISOString()
    };
    
    events.push(eventToSave);
    await setStorageData(ENROLLMENT_HISTORY_KEY, events);
    
    return eventToSave;
  } catch (error) {
    console.error('Error saving enrollment event:', error);
    throw error;
  }
};

/**
 * Track new enrollment
 */
export const trackNewEnrollment = async (
  student: Student, 
  program: Program
): Promise<EnrollmentEvent> => {
  const event: EnrollmentEvent = {
    id: uuidv4(),
    studentId: student.id,
    programId: program.title, // Using title as ID since programs don't have IDs
    eventType: EnrollmentEventType.NEW_ENROLLMENT,
    timestamp: new Date().toISOString(),
    details: {
      programCategory: program.category,
      programTitle: program.title,
      newStatus: EnrollmentStatus.ACTIVE,
      notes: `Initial enrollment in ${program.title}`
    }
  };
  
  // Also log to admin action tracking
  try {
    trackStudentRecordModification(
      student.id,
      'System',
      { program: program.title, category: program.category },
      `New student enrollment in ${program.title}`
    );
  } catch (error) {
    console.error('Failed to log enrollment to admin tracking:', error);
  }
  
  return await saveEnrollmentEvent(event);
};

/**
 * Track program transfer/change
 */
export const trackProgramTransfer = async (
  student: Student,
  fromProgram: Program,
  toProgram: Program,
  notes?: string
): Promise<EnrollmentEvent> => {
  const event: EnrollmentEvent = {
    id: uuidv4(),
    studentId: student.id,
    programId: toProgram.title,
    eventType: EnrollmentEventType.PROGRAM_TRANSFER,
    timestamp: new Date().toISOString(),
    details: {
      fromProgram: {
        category: fromProgram.category,
        title: fromProgram.title
      },
      toProgram: {
        category: toProgram.category,
        title: toProgram.title
      },
      notes: notes || `Transferred from ${fromProgram.title} to ${toProgram.title}`
    }
  };
  
  // Also log to admin action tracking
  try {
    trackStudentRecordModification(
      student.id,
      'System',
      { 
        fromProgram: fromProgram.title, 
        toProgram: toProgram.title 
      },
      `Student transferred from ${fromProgram.title} to ${toProgram.title}`
    );
  } catch (error) {
    console.error('Failed to log transfer to admin tracking:', error);
  }
  
  return await saveEnrollmentEvent(event);
};

/**
 * Track enrollment status change
 */
export const trackStatusChange = async (
  student: Student,
  program: Program,
  previousStatus: EnrollmentStatus,
  newStatus: EnrollmentStatus,
  notes?: string
): Promise<EnrollmentEvent> => {
  const event: EnrollmentEvent = {
    id: uuidv4(),
    studentId: student.id,
    programId: program.title,
    eventType: EnrollmentEventType.STATUS_CHANGE,
    timestamp: new Date().toISOString(),
    details: {
      programCategory: program.category,
      programTitle: program.title,
      previousStatus,
      newStatus,
      notes: notes || `Status changed from ${previousStatus} to ${newStatus}`
    }
  };
  
  // Also log to admin action tracking
  try {
    trackStudentRecordModification(
      student.id,
      'System',
      { previousStatus, newStatus, program: program.title },
      `Student status changed from ${previousStatus} to ${newStatus} in ${program.title}`
    );
  } catch (error) {
    console.error('Failed to log status change to admin tracking:', error);
  }
  
  return await saveEnrollmentEvent(event);
};

/**
 * Track withdrawal/dropout
 */
export const trackWithdrawal = async (
  student: Student,
  program: Program,
  reason?: string,
  notes?: string
): Promise<EnrollmentEvent> => {
  const event: EnrollmentEvent = {
    id: uuidv4(),
    studentId: student.id,
    programId: program.title,
    eventType: EnrollmentEventType.WITHDRAWAL,
    timestamp: new Date().toISOString(),
    details: {
      programCategory: program.category,
      programTitle: program.title,
      reason,
      newStatus: EnrollmentStatus.WITHDRAWN,
      notes: notes || `Withdrawn from ${program.title}`
    }
  };
  
  // Also log to admin action tracking
  try {
    trackStudentRecordModification(
      student.id,
      'System',
      { reason, program: program.title },
      `Student withdrawn from ${program.title}`
    );
  } catch (error) {
    console.error('Failed to log withdrawal to admin tracking:', error);
  }
  
  return await saveEnrollmentEvent(event);
};

/**
 * Track re-enrollment
 */
export const trackReEnrollment = async (
  student: Student,
  program: Program,
  previousEnrollmentDate?: string,
  notes?: string
): Promise<EnrollmentEvent> => {
  const event: EnrollmentEvent = {
    id: uuidv4(),
    studentId: student.id,
    programId: program.title,
    eventType: EnrollmentEventType.RE_ENROLLMENT,
    timestamp: new Date().toISOString(),
    details: {
      programCategory: program.category,
      programTitle: program.title,
      previousEnrollmentDate,
      newStatus: EnrollmentStatus.ACTIVE,
      notes: notes || `Re-enrolled in ${program.title}`
    }
  };
  
  // Also log to admin action tracking
  try {
    trackStudentRecordModification(
      student.id,
      'System',
      { previousEnrollmentDate, program: program.title },
      `Student re-enrolled in ${program.title}`
    );
  } catch (error) {
    console.error('Failed to log re-enrollment to admin tracking:', error);
  }
  
  return await saveEnrollmentEvent(event);
};
